import { useMemo } from 'react';

import { CartesianGrid, LineChart, XAxis, YAxis } from 'recharts';
import type { TooltipProps } from 'recharts';
import type { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';

import { ChartContainer, ChartTooltip, type ChartConfig } from '@/components/ui/chart';
import { formatDate } from '@/util/dateUtils';

import {
  HUMAN_AGENTS_METRIC_CONFIGS,
  type HumanAgentsMetricKey,
  type HumanAgentsTimeseriesPoint,
} from './humanAgentsChartConfig';
import { clampSeriesToDomain } from '../../utils/chartConfig';
import {
  padSeriesToPeriod,
  type PaddedSeriesPoint,
  type PeriodRange,
} from '../../utils/padSeriesToPeriod';
import {
  CHART_COLOR,
  COMPARISON_CHART_COLOR,
  renderTimeseriesLines,
} from '../TimeseriesChart/renderTimeseriesLines';
import {
  MetricTooltipCard,
  MetricTooltipRow,
  MetricTooltipSectionLabel,
} from '../tooltip/MetricTooltipCard';

/**
 * A wide overview point collapsed to the long `{ value }` shape the padding
 * util and the chart line work in. The wide metric fields are carried through
 * for the tooltip; `value` mirrors the selected metric's field. After padding,
 * empty days carry only `date` + `value`, so any wide field may be absent.
 */
type LongPoint = PaddedSeriesPoint<HumanAgentsTimeseriesPoint & { value: number }>;

type MergedPoint = LongPoint & { comparisonValue?: number | null; comparison?: LongPoint };

/** What the tooltip reads off the hovered row (wide fields may be absent on padded empty days). */
type ReviewCounts = Partial<Pick<HumanAgentsTimeseriesPoint, 'userReviews' | 'aiReviews'>>;
interface ChartPayload extends ReviewCounts {
  comparison?: ReviewCounts;
}

const CSAT_REVIEWS_KEY = { userCsat: 'userReviews', aiCsat: 'aiReviews' } as const;

export const HumanAgentsTooltip = ({
  active,
  payload,
  label,
  metric,
  t,
  showComparison,
}: TooltipProps<ValueType, NameType> & {
  metric: HumanAgentsMetricKey;
  t: (key: string) => string;
  showComparison?: boolean;
}) => {
  if (!active || !payload?.length) {
    return null;
  }

  // A gap (empty) day has a null primary value and no data to show.
  const value = payload.find((p) => p.dataKey === 'value')?.value as number | undefined;
  if (value == null) {
    return null;
  }

  const dataPoint = payload[0]?.payload as ChartPayload | undefined;
  const comparisonValue = payload.find((p) => p.dataKey === 'comparisonValue')?.value as
    number | undefined;

  const formattedDate = formatDate(label as string, 'MMM d, yyyy');
  const config = HUMAN_AGENTS_METRIC_CONFIGS[metric];
  const previousPeriodLabel = t('analytics.overview_new.previous_period');
  const hasComparison = showComparison && comparisonValue != null;

  if (metric === 'userCsat' || metric === 'aiCsat') {
    const reviewsKey = CSAT_REVIEWS_KEY[metric];
    return (
      <MetricTooltipCard title={formattedDate}>
        <MetricTooltipRow label={t(config.labelKey)} value={config.tooltipValueFormatter(value)} />
        <MetricTooltipRow
          label={t('analytics.human_agents_overview.tooltip_total_reviews')}
          value={(dataPoint?.[reviewsKey] ?? 0).toLocaleString()}
        />
        {hasComparison && (
          <>
            <MetricTooltipSectionLabel>{previousPeriodLabel}</MetricTooltipSectionLabel>
            <MetricTooltipRow
              label={t(config.labelKey)}
              value={config.tooltipValueFormatter(comparisonValue)}
            />
            <MetricTooltipRow
              label={t('analytics.human_agents_overview.tooltip_total_reviews')}
              value={(dataPoint?.comparison?.[reviewsKey] ?? 0).toLocaleString()}
            />
          </>
        )}
      </MetricTooltipCard>
    );
  }

  if (metric === 'totalConversations') {
    return (
      <MetricTooltipCard title={formattedDate}>
        <MetricTooltipRow
          label={t('analytics.human_agents_overview.tooltip_conversations')}
          value={value.toLocaleString()}
        />
        {hasComparison && (
          <>
            <MetricTooltipSectionLabel>{previousPeriodLabel}</MetricTooltipSectionLabel>
            <MetricTooltipRow
              label={t('analytics.human_agents_overview.tooltip_conversations')}
              value={comparisonValue.toLocaleString()}
            />
          </>
        )}
      </MetricTooltipCard>
    );
  }

  return (
    <MetricTooltipCard title={formattedDate}>
      <MetricTooltipRow
        label={t('analytics.human_agents_overview.avg_resolution_time')}
        value={config.tooltipValueFormatter(value)}
      />
      {hasComparison && (
        <>
          <MetricTooltipSectionLabel>{previousPeriodLabel}</MetricTooltipSectionLabel>
          <MetricTooltipRow
            label={t('analytics.human_agents_overview.avg_resolution_time')}
            value={config.tooltipValueFormatter(comparisonValue)}
          />
        </>
      )}
    </MetricTooltipCard>
  );
};

/** The numeric field each selected metric maps to (excludes the string `date`). */
type NumericPointKey = Exclude<keyof HumanAgentsTimeseriesPoint, 'date'>;

const METRIC_VALUE_KEY: Record<HumanAgentsMetricKey, NumericPointKey> = {
  userCsat: 'userCsat',
  aiCsat: 'aiCsat',
  totalConversations: 'conversations',
  resolutionTime: 'resolutionTimeSeconds',
};

/**
 * Index-aligns the primary series with the comparison series so both render on
 * the same chart. Both are padded to their own (equal-length) periods first, so
 * index N is the same day-offset in each.
 */
const mergeTimeseriesData = (primary: LongPoint[], comparison: LongPoint[]): MergedPoint[] =>
  primary.map((point, index) => {
    const counterpart = comparison[index];
    return { ...point, comparisonValue: counterpart?.value ?? null, comparison: counterpart };
  });

export const HumanAgentsTimeseriesChart = ({
  data,
  comparisonData,
  metric,
  t,
  period,
  comparisonPeriod,
}: {
  data: HumanAgentsTimeseriesPoint[];
  comparisonData?: HumanAgentsTimeseriesPoint[];
  metric: HumanAgentsMetricKey;
  t: (key: string) => string;
  /** The selected date range; drives the x-axis so every day renders, data or not. */
  period: PeriodRange;
  /** The previous-period range. Equal length to `period` by construction; required so the
      comparison series can never render without merged comparison values. */
  comparisonPeriod: PeriodRange;
}) => {
  const config = HUMAN_AGENTS_METRIC_CONFIGS[metric];
  const valueKey = METRIC_VALUE_KEY[metric];

  const showComparison = !!comparisonData && comparisonData.length > 0;

  const chartData = useMemo(() => {
    // Collapse the wide point to a long `value` for the selected metric, then
    // pad to the full period so the x-axis spans every day.
    const toLong = (p: HumanAgentsTimeseriesPoint) => ({ ...p, value: p[valueKey] });
    const paddedPrimary = padSeriesToPeriod(data.map(toLong), period, config.fill);
    const merged = showComparison
      ? mergeTimeseriesData(
          paddedPrimary,
          padSeriesToPeriod((comparisonData ?? []).map(toLong), comparisonPeriod, config.fill)
        )
      : paddedPrimary;
    return clampSeriesToDomain(merged, config.domain, 'value');
  }, [
    data,
    comparisonData,
    valueKey,
    showComparison,
    period,
    comparisonPeriod,
    config.fill,
    config.domain,
  ]);

  const chartConfig = useMemo<ChartConfig>(() => {
    const base: ChartConfig = { value: { label: t(config.labelKey), color: CHART_COLOR } };
    if (showComparison) {
      base.comparisonValue = {
        label: t('analytics.overview_new.previous_period'),
        color: COMPARISON_CHART_COLOR,
      };
    }
    return base;
  }, [config.labelKey, t, showComparison]);

  return (
    <ChartContainer config={chartConfig} className="aspect-4/1 w-full">
      <LineChart data={chartData} margin={{ top: 4, right: 4, bottom: 0, left: 4 }}>
        <CartesianGrid vertical={false} strokeDasharray="3 3" />
        <XAxis
          dataKey="date"
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          tickFormatter={(value) => formatDate(value, 'MMM d')}
          interval="preserveStartEnd"
          minTickGap={40}
        />
        <YAxis
          domain={config.domain}
          ticks={config.ticks}
          tickCount={5}
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          width={80}
          tickFormatter={(value: number) => (config.tickFormatter ?? config.valueFormatter)(value)}
        />
        <ChartTooltip
          content={<HumanAgentsTooltip metric={metric} t={t} showComparison={showComparison} />}
        />
        {renderTimeseriesLines({ fill: config.fill, showComparison })}
      </LineChart>
    </ChartContainer>
  );
};
